import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, useForm } from "react-hook-form";
import { createKycOrgSchema, KycOrgData } from "../KycOrgForm.schema";
import { KycOrgMode } from "../KycOrgForm.types";
import { QueryProfile } from "@/api/queries/types";
import { Contact } from "@/api/types";

// -- Profile lookup helpers ---------------------------------------------------

type ProfileMap = Record<string, string | number | boolean | null>;

function buildProfileMap(rows: QueryProfile[]): ProfileMap {
  const map: ProfileMap = {};
  for (const p of rows) {
    let value: string | number | boolean | null = null;
    switch (p.primaryType ?? "") {
      case "java.lang.Boolean":
        value = p.booleanValue;
        break;
      case "java.lang.Double":
        value = p.doubleValue;
        break;
      case "java.lang.Integer":
      case "java.lang.Long":
        value = p.intValue;
        break;
      default:
        value = p.stringValue ?? null;
    }
    map[p.attributeKey] = value;
    // Index by short key (strip "contact.XXX." namespace prefix)
    const short = p.attributeKey.replace(/^contact\.[^.]+\./, "");
    if (short !== p.attributeKey) map[short] = value;
    // Also index by short key without type prefix, e.g. "string_mifidEsgPreference" -> "mifidEsgPreference"
    const shortNoType = short.replace(/^[a-z]+_/, "");
    if (shortNoType !== short) map[shortNoType] = value;
  }
  return map;
}

const pStr = (map: ProfileMap, key: string) => String(map[key] ?? "");
const pNum = (map: ProfileMap, key: string) => Number(map[key] ?? 0);
const pBool = (map: ProfileMap, key: string) => Boolean(map[key] ?? false);

/** If the stored value starts with "CUSTOM-", returns "other"; otherwise the value as-is. */
const pCustomSel = (map: ProfileMap, key: string): string => {
  const v = pStr(map, key);
  return v.startsWith("CUSTOM-") ? "other" : v;
};
/** Returns the custom text after stripping the "CUSTOM-" prefix, or "" if not a custom value. */
const pCustomText = (map: ProfileMap, key: string): string => {
  const v = pStr(map, key);
  return v.startsWith("CUSTOM-") ? v.slice(7) : "";
};

// -- Default values ----------------------------------------------------------

function buildDefaultValues(
  contact: Contact | null | undefined,
  profileData: QueryProfile[]
): KycOrgData {
  const p = buildProfileMap(profileData);
  const addr = contact?.primaryAddress;

  return {
    // -- Selskapsdata
    orgNumber: contact?.contactId ?? "",
    orgName: contact?.name ?? "",
    address: addr?.address1 ?? "",
    zip: addr?.zipCode ?? "",
    city: addr?.city ?? "",
    phone: addr?.phone1 ?? "",
    email: addr?.email ?? "",
    taxCountry: contact?.taxCountry?.code ?? "",

    // -- Daglig kontakt
    mcId: pStr(p, "mainContact"),
    mcName: pStr(p, "amlContactPerson"),
    mcAddress: pStr(p, "MCaddress"),
    mcZip: pStr(p, "MCzip"),
    mcCity: pStr(p, "MCcity"),
    mcPhone: pStr(p, "MCphone"),
    mcEmail: pStr(p, "MCemail"),
    amlContactPersonPowerOfAttorney: pBool(
      p,
      "amlContactPersonPowerOfAttorney"
    ),
    amlContactPersonPep: pBool(p, "amlContactPersonPep"),

    // -- Reelle rettighetshavere
    amlBeneficialOwner1: {
      ssn: pStr(p, "amlBeneficialOwner1Ssn"),
      name: pStr(p, "amlBeneficialOwner1Name"),
      ownership: pNum(p, "amlBeneficialOwner1Ownership"),
      votingRights: pNum(p, "amlBeneficialOwner1VotingRights"),
      pep: pStr(p, "amlBeneficialOwner1Pep"),
    },
    amlBeneficialOwner2: {
      ssn: pStr(p, "amlBeneficialOwner2Ssn"),
      name: pStr(p, "amlBeneficialOwner2Name"),
      ownership: pNum(p, "amlBeneficialOwner2Ownership"),
      votingRights: pNum(p, "amlBeneficialOwner2VotingRights"),
      pep: pStr(p, "amlBeneficialOwner2Pep"),
    },
    amlBeneficialOwner3: {
      ssn: pStr(p, "amlBeneficialOwner3Ssn"),
      name: pStr(p, "amlBeneficialOwner3Name"),
      ownership: pNum(p, "amlBeneficialOwner3Ownership"),
      votingRights: pNum(p, "amlBeneficialOwner3VotingRights"),
      pep: pStr(p, "amlBeneficialOwner3Pep"),
    },
    amlBeneficialOwner4: {
      ssn: pStr(p, "amlBeneficialOwner4Ssn"),
      name: pStr(p, "amlBeneficialOwner4Name"),
      ownership: pNum(p, "amlBeneficialOwner4Ownership"),
      votingRights: pNum(p, "amlBeneficialOwner4VotingRights"),
      pep: pStr(p, "amlBeneficialOwner4Pep"),
    },
    amlBeneficialOwner5: {
      ssn: pStr(p, "amlBeneficialOwner5Ssn"),
      name: pStr(p, "amlBeneficialOwner5Name"),
      ownership: pNum(p, "amlBeneficialOwner5Ownership"),
      votingRights: pNum(p, "amlBeneficialOwner5VotingRights"),
      pep: pStr(p, "amlBeneficialOwner5Pep"),
    },
    benOwnerComment: pStr(p, "benOwnerComment"),

    // -- AML/KYC
    amlBusinessActivity: pCustomSel(p, "amlBusinessActivity"),
    amlBusinessActivityCustom: pCustomText(p, "amlBusinessActivity"),
    amlPurposeOfCustomerRelationship: pCustomSel(
      p,
      "amlPurposeOfCustomerRelationship"
    ),
    amlPurposeOfCustomerRelationshipCustom: pCustomText(
      p,
      "amlPurposeOfCustomerRelationship"
    ),
    amlInvestedPortfolio: pStr(p, "amlInvestedPortfolio"),
    amlAnnualDeposits: pStr(p, "amlAnnualDeposits"),
    amlSourceOfFunds: pCustomSel(p, "amlSourceOfFunds"),
    amlSourceOfFundsCustom: pCustomText(p, "amlSourceOfFunds"),
    amlSourceOfWealth: pStr(p, "amlSourceOfWealth"),
    amlManagedOnBehalfOfOthers: pStr(p, "amlManagedOnBehalfOfOthers"),
    amlTransfersAbroad: pCustomSel(p, "amlTransfersAbroad"),
    amlTransfersAbroadCustom: pCustomText(p, "amlTransfersAbroad"),
    amlCryptoCurrency: pStr(p, "amlCryptoCurrency"),

    // -- MIFID instruments
    mifidKnowledgeOfMutualFunds: pStr(p, "mifidKnowledgeOfMutualFunds"),
    mifidExperienceOfMutualFunds: pStr(p, "mifidExperienceOfMutualFunds"),
    mifidTradingFrequencyOfMutualFunds: pStr(
      p,
      "mifidTradingFrequencyOfMutualFunds"
    ),
    mifidPeriodOfMutualFunds: pStr(p, "mifidPeriodOfMutualFunds"),
    mifidKnowledgeOfListedStocks: pStr(p, "mifidKnowledgeOfListedStocks"),
    mifidExperienceOfListedStocks: pStr(p, "mifidExperienceOfListedStocks"),
    mifidTradingFrequencyOfListedStocks: pStr(
      p,
      "mifidTradingFrequencyOfListedStocks"
    ),
    mifidPeriodOfListedStocks: pStr(p, "mifidPeriodOfListedStocks"),
    mifidKnowledgeOfUnlistedStocks: pStr(p, "mifidKnowledgeOfUnlistedStocks"),
    mifidExperienceOfUnlistedStocks: pStr(p, "mifidExperienceOfUnlistedStocks"),
    mifidTradingFrequencyOfUnlistedStocks: pStr(
      p,
      "mifidTradingFrequencyOfUnlistedStocks"
    ),
    mifidPeriodOfUnlistedStocks: pStr(p, "mifidPeriodOfUnlistedStocks"),
    mifidKnowledgeOfBonds: pStr(p, "mifidKnowledgeOfBonds"),
    mifidExperienceOfBonds: pStr(p, "mifidExperienceOfBonds"),
    mifidTradingFrequencyOfBonds: pStr(p, "mifidTradingFrequencyOfBonds"),
    mifidPeriodOfBonds: pStr(p, "mifidPeriodOfBonds"),
    mifidKnowledgeOfComplexInvestments: pStr(
      p,
      "mifidKnowledgeOfComplexInvestments"
    ),
    mifidExperienceOfComplexInvestments: pStr(
      p,
      "mifidExperienceOfComplexInvestments"
    ),
    mifidTradingFrequencyOfComplexInvestments: pStr(
      p,
      "mifidTradingFrequencyOfComplexInvestments"
    ),
    mifidPeriodOfComplexInvestments: pStr(p, "mifidPeriodOfComplexInvestments"),
    mifidRelevantExperienceEducation: pStr(
      p,
      "mifidRelevantExperienceEducation"
    ),

    // -- MIFID financial
    mifidTotalNetWealth: pStr(p, "mifidTotalNetWealth"),
    mifidLiquidInvestableWealth: pStr(p, "mifidLiquidInvestableWealth"),
    mifidGrossAnnualIncome: pStr(p, "mifidGrossAnnualIncome"),
    mifidLiquidityBuffer: pStr(p, "mifidLiquidityBuffer"),
    mifidDebtBurden: pStr(p, "mifidDebtBurden"),
    mifidIncomeStability: pStr(p, "mifidIncomeStability"),
    mifidEarliestWithdrawal: pStr(p, "mifidEarliestWithdrawal"),

    // -- MIFID risk and purpose
    mifidMainInvestmentPurpose: pStr(p, "mifidMainInvestmentPurpose"),
    mifidRiskToleranceVolatility: pStr(p, "mifidRiskToleranceVolatility"),
    mifidLiquidityNeed: pStr(p, "mifidLiquidityNeed"),
    mifidMarketDropResponse: pStr(p, "mifidMarketDropResponse"),
    mifidValueDeclineImpact: pStr(p, "mifidValueDeclineImpact"),

    // -- MIFID ESG
    mifidEsgPreference: pStr(p, "mifidEsgPreference"),
    mifidEsgPreferenceType: pStr(p, "mifidEsgPreferenceType"),
  };
}

// -- Hook --------------------------------------------------------------------

export function useKycOrgForm(
  contact?: Contact | null,
  profileData: QueryProfile[] = [],
  mode: KycOrgMode = "combined"
) {
  const schema = useMemo(() => createKycOrgSchema(mode), [mode]);

  const defaultValues = useMemo(
    () => buildDefaultValues(contact, profileData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contact?.id, profileData.length]
  );

  return useForm<KycOrgData>({
    resolver: zodResolver(schema) as Resolver<KycOrgData>,
    values: defaultValues,
  });
}
